import { useTranslation } from '@/i18n';

import { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import CustomAutomations from '@/ui/components/customAutomations/CustomAutomations';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import DataIndicatorCard from '@payaca/components/dataIndicatorCard/DataIndicatorCard';
import { getKeyIndicatorsData } from '@payaca/store/analytics/analyticsActions';
import { requestGetAccountPipelines } from '@payaca/store/pipelines/pipelineActions';
import {
  KeyIndicatorData,
  KeyIndicatorType,
} from '@payaca/types/analyticsTypes';
import moment from 'moment-timezone';
import { useDispatch } from 'react-redux';

const AutomationsPage: FC = () => {
  const translate = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(requestGetAccountPipelines());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  return (
    <>
      <Helmet title={translate('pages.automations.title')} />
      <AuthenticatedPageWrapper>
        <div className="p-4">
          <AutomationKeyIndicatorsData />
        </div>
        <div className="p-4 pb-6">
          <CustomAutomations />
        </div>
      </AuthenticatedPageWrapper>
    </>
  );
};

export default AutomationsPage;

const AutomationKeyIndicatorsData: FC = () => {
  const dispatch = useDispatch();

  const [keyIndicatorsData, setKeyIndicatorsData] = useState<
    KeyIndicatorData<KeyIndicatorType>[]
  >([]);

  useEffect(() => {
    dispatch(
      getKeyIndicatorsData.request({
        indicatorTypes: [
          KeyIndicatorType.AUTOMATION_SMS_NOTIFICATIONS,
          KeyIndicatorType.AUTOMATION_EMAIL_NOTIFICATIONS,
        ],
        callback: setKeyIndicatorsData,
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const smsNotificationIndicatorData = keyIndicatorsData?.find(
    (x) => x.type == KeyIndicatorType.AUTOMATION_SMS_NOTIFICATIONS
  );

  const emailNotificationIndicatorData = keyIndicatorsData?.find(
    (x) => x.type == KeyIndicatorType.AUTOMATION_EMAIL_NOTIFICATIONS
  );

  const indicatorCardWrapperClasses = 'grow basis-0 max-w-xl min-w-sm';

  return (
    <div className="flex flex-row flex-wrap gap-8 w-full">
      {emailNotificationIndicatorData && (
        <div className={indicatorCardWrapperClasses}>
          <DataIndicatorCard
            title="Email automations"
            iconName="mail-05.3"
            periodLengthInDays={
              emailNotificationIndicatorData.periodLengthInDays
            }
            percentageChange={emailNotificationIndicatorData.percentageChange}
            sparkLineData={emailNotificationIndicatorData.sparkLineData}
            currentPeriod={emailNotificationIndicatorData.currentPeriod}
            CustomLabelledValue={({ value }) => {
              return (
                <>
                  <DataIndicatorCard.LabelledValue>
                    <DataIndicatorCard.ValueLabel>
                      Sent
                    </DataIndicatorCard.ValueLabel>
                    <DataIndicatorCard.Value>{value}</DataIndicatorCard.Value>
                  </DataIndicatorCard.LabelledValue>
                  <TimeSavedLabelledValue timeSavedInMinutes={value * 10} />
                </>
              );
            }}
          />
        </div>
      )}
      {smsNotificationIndicatorData && (
        <div className={indicatorCardWrapperClasses}>
          <DataIndicatorCard
            title="SMS automations"
            iconName="notification-message"
            periodLengthInDays={smsNotificationIndicatorData.periodLengthInDays}
            percentageChange={smsNotificationIndicatorData.percentageChange}
            sparkLineData={smsNotificationIndicatorData.sparkLineData}
            currentPeriod={smsNotificationIndicatorData.currentPeriod}
            CustomLabelledValue={({ value }) => {
              return (
                <>
                  <DataIndicatorCard.LabelledValue>
                    <DataIndicatorCard.ValueLabel>
                      Sent
                    </DataIndicatorCard.ValueLabel>
                    <DataIndicatorCard.Value>{value}</DataIndicatorCard.Value>
                  </DataIndicatorCard.LabelledValue>
                  <TimeSavedLabelledValue timeSavedInMinutes={value * 5} />
                </>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

const TimeSavedLabelledValue = ({
  timeSavedInMinutes,
}: {
  timeSavedInMinutes: number;
}) => {
  const duration = moment.duration(timeSavedInMinutes, 'minutes');

  const hours = duration.hours();
  const minutes = duration.minutes();

  if (!timeSavedInMinutes) return null;

  return (
    <DataIndicatorCard.LabelledValue>
      <DataIndicatorCard.ValueLabel>Time saved</DataIndicatorCard.ValueLabel>
      <DataIndicatorCard.Value>
        {!!hours && (
          <>
            {hours}
            <span className="text-base font-extralight"> hrs</span>{' '}
          </>
        )}{' '}
        {!!minutes && (
          <>
            {minutes}
            <span className="text-base font-extralight"> mins</span>{' '}
          </>
        )}{' '}
      </DataIndicatorCard.Value>
    </DataIndicatorCard.LabelledValue>
  );
};
